import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, View, TouchableOpacity, Dimensions, FlatList } from 'react-native';
import { VideoView, useVideoPlayer } from 'expo-video';
import YoutubePlayer from '../../../components/Community/YoutubeInlinePlayer';
import { Text } from '../../../components/ui/AppText';
import { getVideoPublicUrl, incrementVideoView } from '../services/communityVideosService';
import { VIDEO_SOURCE } from '../constants';
import { styles } from './styles';

const { width: SCREEN_W } = Dimensions.get('window');
const YOUTUBE_HEIGHT = Math.round(SCREEN_W * (9 / 16)); // 16:9

// ── Modo vertical (Shorts) ───────────────────────────────────────────────────
// A lib trava a área do player em 16:9 com `padding-bottom: 56.25%` no HTML que
// ela injeta. Esse HTML é do NOSSO documento (só o iframe do YouTube é de outro
// domínio), então dá para reescrever o CSS por injeção — e o player passa a
// receber uma área 9:16 DE VERDADE.
//
// Por que assim, e não aumentando a caixa: alargar a caixa e recortar as sobras
// faz o vídeo crescer, mas joga a chrome do YouTube (título, barra de progresso,
// play/pause) para fora da tela, porque ela é posicionada em relação à caixa.
// Corrigindo a proporção na origem, os controles do PRÓPRIO YouTube ficam com a
// largura da tela e continuam funcionando.
const VERTICAL_VIDEO_H = Math.round(SCREEN_W * (16 / 9));

// Regra de CSS injetada ANTES do conteúdo existir. É o que evita o pulo visual:
// se a correção só rodasse depois do load, a página renderizaria um instante em
// 16:9 e só então esticaria para 9:16 — percebido como "o vídeo aparece pequeno
// e depois expande". Como é uma regra `!important` no documento, ela já vale
// quando a `.container` é criada.
const VERTICAL_CSS_BEFORE_LOAD = `
  (function () {
    var css = '.container { padding-bottom: 177.78% !important; }';
    var st = document.createElement('style');
    st.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(st);
  })();
  true;
`;

// Reforço para o caso de o "antes do conteúdo" não disparar (varia por versão de
// WebView no Android). Aqui a correção chega tarde, mas chega.
const VERTICAL_CONTAINER_PATCH = `
  (function () {
    var apply = function () {
      var c = document.querySelector('.container');
      if (c) { c.style.setProperty('padding-bottom', '177.78%', 'important'); }
    };
    apply();
    document.addEventListener('DOMContentLoaded', apply);
  })();
  true;
`;

/**
 * Uma página do reel (1 vídeo, tela cheia). Só reproduz quando é a página ativa;
 * ao terminar, dispara onEnded para o pai avançar para o próximo.
 */
const ReelItem = ({ video, isActive, height, vertical, onEnded }) => {
  const isYoutube = video?.source === VIDEO_SOURCE.YOUTUBE;

  // ⚠️ No modo vertical o player NASCE SEM VÍDEO e recebe o id no `onReady`.
  // Mesmo tratamento já validado no FeedVideoOverlay: montar direto com o id
  // manda um `playVideo()` sobre um player recém-criado, que NÃO pega; trocar o
  // id com o player pronto chama `loadVideoById(id, play)`, que carrega e toca.
  // Sem isto, e com a chrome desligada, o vídeo fica parado e sem botão de play.
  // Gateado em `vertical` de propósito: "Mais relevantes" e o Perfil seguem
  // exatamente como estavam.
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const youtubeId = video?.youtubeId;
  const effectiveYoutubeId = vertical && !isPlayerReady ? '' : youtubeId;

  // Ao sair de foco o player é DESMONTADO (ver render). Zerar o "pronto" aqui é
  // o que faz a próxima montagem repetir o ciclo nasce-sem-vídeo → `onReady` →
  // `loadVideoById`; sem isto, ao voltar ele montaria já com o id e cairia de
  // novo no `playVideo()` que não pega.
  useEffect(() => {
    if (vertical && !isActive) setIsPlayerReady(false);
  }, [vertical, isActive]);

  const uploadUrl = !isYoutube && video?.storagePath ? getVideoPublicUrl(video.storagePath) : null;

  const player = useVideoPlayer(uploadUrl, (p) => {
    p.loop = false;
  });

  // Toca só a página ativa (do começo); pausa as demais.
  useEffect(() => {
    if (isYoutube) return;
    if (isActive) {
      player.currentTime = 0;
      player.play();
    } else {
      player.pause();
    }
  }, [isActive, isYoutube, player]);

  // Avanço automático ao terminar (upload).
  useEffect(() => {
    if (isYoutube) return;
    const sub = player.addListener('playToEnd', () => onEnded?.());
    return () => sub.remove();
  }, [isYoutube, player, onEnded]);

  const handleYoutubeState = useCallback(
    (state) => {
      if (state === 'ended') onEnded?.();
    },
    [onEnded]
  );

  return (
    <View style={[styles.reelPage, { height }]}>
      {isYoutube ? (
        video?.youtubeId ? (
          // No modo vertical, SÓ o item ativo mantém o player montado. Manter os
          // vizinhos vivos deixava o áudio do vídeo anterior tocando ao deslizar:
          // a pausa depende de uma mensagem que a lib envia ao WebView, e ela não
          // se mostrou confiável aqui (o caminho de galeria não sofre porque
          // chama `player.pause()` direto). Desmontar é determinístico — sem
          // WebView, não há som.
          //
          // Inativo NÃO renderiza pôster, de propósito. Tentei com a miniatura e
          // saiu pior: a `hqdefault` do YouTube é 16:9 com o vídeo vertical em
          // tarjas laterais, então numa página 9:16 ela vira uma faixa larga que
          // não lembra o vídeo — e a sequência miniatura → preto → vídeo dava três
          // trocas visuais (o "flash" relatado). Fundo preto = uma troca só.
          !vertical || isActive ? (
          <YoutubePlayer
            height={vertical ? VERTICAL_VIDEO_H : YOUTUBE_HEIGHT}
            width={SCREEN_W}
            play={isActive}
            videoId={effectiveYoutubeId}
            onReady={() => setIsPlayerReady(true)}
            initialPlayerParams={{ modestbranding: true, rel: false }}
            onChangeState={handleYoutubeState}
            onError={() => {}}
            // Corrige a proporção da área do player na origem (ver o patch no
            // topo). Só no modo vertical; os demais consumidores não recebem.
            webViewProps={
              vertical
                ? {
                    injectedJavaScriptBeforeContentLoaded: VERTICAL_CSS_BEFORE_LOAD,
                    injectedJavaScript: VERTICAL_CONTAINER_PATCH,
                    // Sem isto o WebView rola por dentro e chega a mostrar barra
                    // de rolagem sobre o vídeo.
                    scrollEnabled: false,
                  }
                : undefined
            }
          />
          ) : null
        ) : (
          <Text style={styles.playerError}>Vídeo indisponível</Text>
        )
      ) : (
        <VideoView
          player={player}
          style={styles.reelVideo}
          contentFit="contain"
          nativeControls
          allowsFullscreen
        />
      )}
    </View>
  );
};

/**
 * Player full-screen estilo reel/Stories:
 *  - avança sozinho quando o vídeo termina (auto-advance);
 *  - deslizar na vertical troca de vídeo;
 *  - pede mais ao chegar perto do fim da lista (onEndReached).
 */
const VideoPlayerModal = ({
  videos = [],
  initialIndex = 0,
  visible,
  onClose,
  onEndReached,
  // Opt-in. `false` mantém EXATAMENTE o comportamento anterior — "Mais relevantes"
  // e a lista do Perfil usam o mesmo player e seguem intocados.
  vertical = false,
}) => {
  const list = Array.isArray(videos) ? videos : [];
  const listRef = useRef(null);
  const activeIndexRef = useRef(initialIndex);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [pageH, setPageH] = useState(0);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  // Ao abrir, posiciona no vídeo escolhido.
  useEffect(() => {
    if (visible) {
      setActiveIndex(initialIndex);
      activeIndexRef.current = initialIndex;
    }
  }, [visible, initialIndex]);

  // Conta 1 view sempre que um novo vídeo fica ativo (com o modal aberto).
  useEffect(() => {
    const v = list[activeIndex];
    if (visible && v) incrementVideoView(v.id).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, visible]);

  // Carrega mais quando faltam ≤2 itens à frente.
  useEffect(() => {
    if (visible && activeIndex >= list.length - 2) onEndReached?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, visible]);

  const viewabilityConfig = useRef({ itemVisiblePercentThreshold: 80 }).current;
  const onViewableItemsChanged = useRef(({ viewableItems }) => {
    const first = viewableItems?.[0];
    if (first?.index != null) setActiveIndex(first.index);
  }).current;

  const goNext = useCallback(() => {
    const next = activeIndexRef.current + 1;
    if (next < list.length) listRef.current?.scrollToIndex({ index: next, animated: true });
  }, [list.length]);

  const getItemLayout = useCallback(
    (_, index) => ({ length: pageH, offset: pageH * index, index }),
    [pageH]
  );

  const renderItem = useCallback(
    ({ item, index }) => (
      <ReelItem
        video={item}
        isActive={visible && index === activeIndex}
        height={pageH}
        vertical={vertical}
        onEnded={goNext}
      />
    ),
    [visible, activeIndex, pageH, vertical, goNext]
  );

  return (
    <Modal visible={visible} animationType="fade" onRequestClose={onClose}>
      <View
        style={styles.reelBackdrop}
        onLayout={(e) => {
          const h = e.nativeEvent.layout.height;
          if (h && h !== pageH) setPageH(h);
        }}
      >
        <TouchableOpacity style={styles.playerClose} onPress={onClose} hitSlop={12}>
          <Text style={styles.playerCloseText}>✕</Text>
        </TouchableOpacity>

        {visible && pageH > 0 && list.length > 0 && (
          <FlatList
            ref={listRef}
            style={styles.reelList}
            data={list}
            keyExtractor={(item) => item.id}
            renderItem={renderItem}
            extraData={activeIndex}
            pagingEnabled
            showsVerticalScrollIndicator={false}
            initialScrollIndex={Math.min(initialIndex, list.length - 1)}
            getItemLayout={getItemLayout}
            onViewableItemsChanged={onViewableItemsChanged}
            viewabilityConfig={viewabilityConfig}
            windowSize={3}
            maxToRenderPerBatch={2}
            initialNumToRender={1}
            removeClippedSubviews={false}
            decelerationRate="fast"
            onEndReachedThreshold={0.5}
            onEndReached={onEndReached}
          />
        )}
      </View>
    </Modal>
  );
};

export default VideoPlayerModal;
